/**
 * Auth context for the conveyancer portal.
 *
 * Wraps the centralised api client (`services/api.js`) with React state
 * for the current user, the bearer token, and a `loading` flag the
 * routes use to gate their first render.
 *
 * Public surface:
 *   const { user, token, loading, login, register, logout, refreshUser } = useAuth();
 *
 * The `bc:auth:logout` window event lets api.js force a logout from a
 * 401 response without importing this module (the api → context import
 * cycle would otherwise be unavoidable).
 */

import { createContext, useContext, useEffect, useState } from "react";

import api from "../services/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  // Initial user fetch on mount or whenever the token changes.
  useEffect(() => {
    let cancelled = false;
    const fetchUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await api.get("/auth/me");
        if (!cancelled) setUser(res.data);
      } catch (err) {
        if (!cancelled) {
          // 401 will fire the logout event below; for any other error
          // we still drop to a logged-out state since we can't trust
          // the cached token.
          handleLogout();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchUser();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Listen for forced logouts from the api 401 interceptor.
  useEffect(() => {
    const onForcedLogout = () => handleLogout();
    window.addEventListener("bc:auth:logout", onForcedLogout);
    return () => window.removeEventListener("bc:auth:logout", onForcedLogout);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (
    email,
    name,
    password,
    province,
    popiaConsent = false,
    codeOfConductAccepted = false,
    termsOfServiceAccepted = false,
  ) => {
    const res = await api.post("/auth/register", {
      email,
      name,
      password,
      province,
      popia_consent: popiaConsent,
      code_of_conduct_accepted: codeOfConductAccepted,
      terms_of_service_accepted: termsOfServiceAccepted,
    });
    const { access_token, user: userData } = res.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  // Apply an already-issued auth result (token + user) to the context and
  // localStorage. Used by the onboarding wizard's new-registration path, where
  // the account is created at the final submit via /auth/register-complete and
  // the returned token must be live before the follow-up refreshUser()/nav.
  const applyAuthResult = ({ access_token, user: userData }) => {
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const refreshUser = async () => {
    const res = await api.get("/auth/me");
    setUser(res.data);
    return res.data;
  };

  const logout = handleLogout;

  return (
    <AuthContext.Provider
      value={{ user, token, loading, login, register, logout, refreshUser, applyAuthResult }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
